<template>
  <div id="AccountSharedRulesetsList" class="Main-Content">
    <Page
      :title="$t('Shared Rulesets')"
      :primaryAction="{
        label: $t('AccountSharedRulesets.New Ruleset'),
        onAction: () => createRuleset(),
        disabled: saving
      }"
      :secondaryActions="[{
        label: $t('Notes'),
        onAction: () => eventHub.$emit('showAccountNotes', 'SHAREDRULESET')
      }]"
    >
      <LoadingLayout v-if="loading" />

      <Layout v-if="!loading">
        <LayoutSection>
          <Card>
            <Filters :query.sync="query" />
            <DataTable
              :columns="[
                {
                  key: 'name',
                  label: $t('Ruleset'),
                  sortable: true,
                  fixed: true
                },
                {
                  key: 'rulesLength',
                  label: $t('Rules')
                },
                {
                  key: 'references',
                  label: $t('References')
                },
                {
                  key: 'updatedOn',
                  label: $t('Last Updated'),
                  format: 'dateLong',
                  sortable: true
                }
              ]"
              :row-actions="[
                {
                  label: $t('Rename'),
                  onAction: (row) => renameRuleset(row),
                  disabled: saving
                },
                {
                  label: $t('Copy'),
                  onAction: (row) => copyRuleset(row),
                  disabled: saving
                },
                {
                  label: $t('Delete'),
                  onAction: (row) => deleteRuleset(row),
                  disabled: saving
                }
              ]"
              :rows="formattedRulesets"
              :sort.sync="sortBy"
              :pagination="{
                offset: offset,
                limit: limit,
                total: formattedRulesets.length,
                updateOffset: val => offset = val,
                updateLimit: updateLimit
              }"
            >
              <template v-slot:col:name="{row}">
                <PageLink :href="`/#/account/sharedRulesets/edit/${row.id}`">{{row.name}}</PageLink>
              </template>
              <template v-slot:col:references="{row}">
                <PageLink v-if="row.references" @click="showModal(row)">{{row.references}}</PageLink>
                <template v-else>{{$t('Not in use')}}</template>
              </template>
            </DataTable>
          </Card>
        </LayoutSection>
      </Layout>
    </Page>

    <Modal
      v-if="modalRuleset"
      :title="$t('AccountSharedRulesets.rulesetReferences', { name: modalRuleset.name})"
      :visible="!!modalRuleset"
      @close="modalRuleset = null"
    >
     <Layout>
       <LayoutSection>
         <Card class="modal-card">
          <DataTable
            :columns="[
              {
                label: $t('Campaigns'),
                key: 'name'
              },
              {
                label: $t('Segments'),
                key: 'segments'
              }
            ]"
            :rows="formattedCampaignSegments"
          >
            <template v-slot:col:name="{row}">
              <a :href="`/#/campaigns/${row.id}`" target="_blank">{{row.name}}</a>
            </template>
            <template v-slot:col:segments="{row}">
              <List tight>
                <ListItem v-for="seg in row.segments" :key="seg.id">
                  <a :href="`/#/campaigns/${row.id}/segmentation/segment/${seg.id}`" target="_blank">{{seg.name}}</a>
                </ListItem>
              </List>
            </template>
          </DataTable>
         </Card>
       </LayoutSection>
     </Layout>
    </Modal>
  </div>
</template>


<script>
import _ from 'lodash';

export default {
  name: 'AccountSharedRulesetsList',
  data () {
    return {
      loading: true,
      saving: false,
      rulesets: [],
      offset: 0,
      limit: 10,
      sortBy: {
        column: 'updatedOn',
        direction: 'desc'
      },
      query: null,
      modalRuleset: null
    }
  },
  mounted() {
    NProgress.start()
    this.limit = this.getTableLimit('tableLimit') || 10
    this.api.getSharedRulesets()
      .then((data) => {
        this.rulesets = data
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    formattedRulesets() {
      let rsArr = this.rulesets.map(rs => {
        return {
          ...rs,
          rulesLength: rs.rules.length,
          references: rs.refs
        }
      })
      if (this.sortBy) {
        let iteree = this.sortBy.column
        if (this.sortBy.column === 'name') {
          iteree = rs => rs.name && rs.name.trim().toLowerCase()
        }
        rsArr = _.orderBy(rsArr, [iteree], [this.sortBy.direction])
      }
      if (this.query) {
        rsArr = rsArr.filter(row => JSON.stringify(row).toLowerCase().indexOf(this.query.toLowerCase()) >= 0)
      }
      return rsArr
    },
    formattedCampaignSegments() {
      return this.modalRuleset && this.modalRuleset.campaigns
    }
  },
  methods: {
    showModal(ruleset) {
      this.modalRuleset = ruleset
    },
    editRuleset(ruleset) {
      this.$router.push(`/account/sharedRulesets/edit/${ruleset.id}`)
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    getSegmentUrl(campaign, segment) {
      return `/#/campaigns/${campaign.id}/segmentation/segment/${segment.id}`
    },
    async createRuleset() {
      const newRulesetName = await this.$ui.prompt({
        title: this.$t('AccountSharedRulesets.Add New Shared Ruleset'),
        content: this.$t('AccountSharedRulesets.Enter New Ruleset Name'),
        primaryActionLabel: this.$t('Create'),
        inputPlaceholder: this.$t('AccountSharedRulesets.Ruleset name')
      })
      if (newRulesetName && newRulesetName.trim()) {
        NProgress.start()
        this.saving = true
        this.api.createAccountRuleset(newRulesetName)
          .then((rs) => {
            this.editRuleset(rs)
            this.$ui.toast({ title: this.$t('AccountSharedRulesets.Ruleset Created') })
          })
          .catch(this.apiCatch)
          .finally(() => {
            this.saving = false
            NProgress.done()
          })
      }
    },
    async copyRuleset(copiedRuleset) {
      const copiedRulesetName = await this.$ui.prompt({
        title: this.$t('AccountSharedRulesets.Copy Ruleset'),
        content: this.$t('AccountSharedRulesets.Enter New Ruleset Name'),
        primaryActionLabel: this.$t('Create'),
        inputValue: copiedRuleset.name,
        inputPlaceholder: this.$t('AccountSharedRulesets.Ruleset name')
      })
      if (copiedRulesetName && copiedRulesetName.trim()) {
        NProgress.start()
        this.saving = true
        this.api.copyAccountRuleset(copiedRuleset, copiedRulesetName)
          .then((rs) => {
            this.editRuleset(rs)
            this.$ui.toast({ title: this.$t('AccountSharedRulesets.Ruleset Copied') })
          })
          .catch(this.apiCatch)
          .finally(() => {
            NProgress.done()
            this.saving = false
          })
      }
    },
    async renameRuleset(ruleset) {
      const renamedRulesetName = await this.$ui.prompt({
        title: this.$t('AccountSharedRulesets.Rename Ruleset'),
        content: this.$t('AccountSharedRulesets.Enter New Ruleset Name'),
        primaryActionLabel: this.$t('Save'),
        inputValue: ruleset.name,
        inputPlaceholder: this.$t('AccountSharedRulesets.Ruleset name')
      })
      if (renamedRulesetName && renamedRulesetName.trim()) {
        NProgress.start()
        this.saving = true
        ruleset.name = renamedRulesetName.trim()
        this.api.updateAccountRuleset(ruleset)
          .then(() => {
            const rs = this.rulesets.find(r => r.id === ruleset.id)
            rs.name = renamedRulesetName.trim()
            this.$ui.toast({ title: this.$t('Name Changed') })
          })
          .catch(this.apiCatch)
          .finally(() => {
            NProgress.done()
            this.saving = false
          })
      }
    },
    async deleteRuleset(ruleset) {
      const confirm = await this.$ui.confirm({
        title: this.$t('Confirm Delete'),
        content: this.$t('AccountSharedRulesets.Are you sure you want to delete shared ruleset', {value: ruleset.name})
      })
      if (!confirm) {
        return
      }
      NProgress.start()
      this.saving = true
      this.api.deleteAccountRuleset(ruleset)
        .then(() => {
          this.rulesets = this.rulesets.filter(r => r.id !== ruleset.id)
          this.$ui.toast({ title: this.$t('AccountSharedRulesets.Ruleset Deleted') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          NProgress.done()
          this.saving = false
        })
    }
  }
}

</script>

<style lang="scss">
#AccountSharedRulesetsList {
  .modal-card {
    height: 500px;
    overflow-y: scroll;
  }
}
</style>
